<template>
  <MetaHead :title="$t('version.update_page.title')" />
  <ProjectHeader :meta="projectMeta" />
  <div class="version-update-page">
    <div class="relative w-full core-wrapper md:px-12 lg:px-24">
      <div class="version-update-card p-8 max-w-2xl mx-auto">
        <h2 class="mb-4 font-semibold text-xl text-neutral-900">{{ title }}</h2>

        <!-- List affected parts -->
        <div v-if="enrichedUpdates?.length" class="mb-4">
          <p class="mb-2 text-sm font-medium text-neutral-700">
            {{ $t('version.update_page.affected_parts') }}
          </p>
          <ul class="text-sm text-neutral-550 list-disc pl-5">
            <li v-for="eu in enrichedUpdates" :key="eu.update.part">
              {{ getPartTitle(eu.update.part, wtGeneral) }} v{{ eu.update.version }}
            </li>
          </ul>
        </div>

        <p v-if="hasCoordinationUpdate" class="mb-4 text-base text-neutral-550">
          {{ wtSetup('coordination', 'update') }}
        </p>

        <!-- PATCH layout -->
        <template v-if="highestLevel === 'patch'">
          <p class="mb-6 text-base text-neutral-550">
            {{ $t('version.update_page.message_patch') }}
          </p>
          <div class="flex flex-col sm:flex-row gap-3">
            <button
              v-if="anyHasStoredResults"
              class="core-button-supporting core-action-button core-action-button--supporting h-11 px-6 flex-1 min-w-fit whitespace-nowrap"
              @click="viewOldSummary">
              {{ $t('version.update_page.view_summary_button') }}
            </button>
            <button
              class="core-button-primary core-action-button core-action-button--primary h-11 px-6 flex-1 min-w-fit whitespace-nowrap"
              @click="acceptUpdate">
              {{ $t('version.update_page.accept_update_button') }}
            </button>
          </div>
        </template>

        <!-- MINOR layout -->
        <template v-else-if="highestLevel === 'minor'">
          <p class="mb-2 text-base text-neutral-550">
            {{ $t('version.update_page.message_minor') }}
          </p>
          <p class="mb-6 text-base text-neutral-550">
            {{ $t('version.update_page.message_minor_hint') }}
          </p>
          <div class="flex flex-col sm:flex-row gap-3">
            <button
              v-if="anyHasStoredResults"
              class="core-button-supporting core-action-button core-action-button--supporting h-11 px-6 flex-1 min-w-fit whitespace-nowrap"
              @click="viewOldSummary">
              {{ $t('version.update_page.view_summary_button') }}
            </button>
            <button
              class="core-button-primary core-action-button core-action-button--primary h-11 px-6 flex-1 min-w-fit whitespace-nowrap"
              @click="acceptUpdate">
              {{ $t('version.update_page.accept_update_button') }}
            </button>
          </div>
        </template>

        <!-- MAJOR layout -->
        <template v-else-if="highestLevel === 'major'">
          <p class="mb-2 text-base text-neutral-550">
            {{ $t('version.update_page.message_major') }}
          </p>
          <template v-if="anyHasStoredResults">
            <p class="mb-6 text-base text-neutral-550">
              {{ $t('version.update_page.message_major_hint') }}
            </p>
            <button
              class="core-button-primary core-action-button core-action-button--primary h-11 px-6 whitespace-nowrap"
              @click="viewOldSummary">
              {{ $t('version.update_page.view_summary_button') }}
            </button>
          </template>
          <p v-else class="mb-6 text-base text-neutral-550">
            {{ $t('version.update_page.message_legacy_hint') }}
          </p>
        </template>

        <!-- LEGACY layout (no level detected, no stored results) -->
        <template v-else>
          <p class="mb-2 text-base text-neutral-550">
            {{ $t('version.update_page.message_legacy') }}
          </p>
          <p class="mb-6 text-base text-neutral-550">
            {{ $t('version.update_page.message_legacy_hint') }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { coordinationEnabledForVersion } from '@arcexperts/core-types'
import { useContentStore } from '@/stores/content'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useProjectStore } from '@/stores/project'
import { getPartTitle } from '@/helpers/shared'
import { useWidgetText } from '@/helpers/useWidgetText'
import MetaHead from '@/components/partials/display/MetaHead.vue'
import ProjectHeader from '@/components/partials/navigation/ProjectHeader.vue'

const { t } = useI18n()
const { wt: wtGeneral } = useWidgetText('widget-texts-general')
const { wt: wtSetup } = useWidgetText('widget-texts-project-setup', 'SPECIALIST_COORDINATION')
const contentStore = useContentStore()
const hasCoordinationUpdate = computed(
  () =>
    !coordinationEnabledForVersion(
      projectStore.getVersion('specialist_coordination_setup'),
      contentStore.coordinationSettings
    ) &&
    projectStore.getAvailableUpdates?.some(
      (update) =>
        update.part === 'specialist_coordination_setup' &&
        coordinationEnabledForVersion(update.version, contentStore.coordinationSettings)
    )
)
const router = useRouter()
const projectStore = useProjectStore()

const projectMeta = computed(() => projectStore.getMeta ?? undefined)
const enrichedUpdates = computed(() => projectStore.getEnrichedUpdates)
const highestLevel = computed(() => projectStore.getHighestUpdateLevel)

const anyHasStoredResults = computed(() => enrichedUpdates.value?.some((eu) => eu.hasStoredResults) ?? false)

const title = computed(() => {
  switch (highestLevel.value) {
    case 'patch':
      return t('version.update_page.title_patch')
    case 'minor':
      return t('version.update_page.title_minor')
    case 'major':
      return t('version.update_page.title_major')
    default:
      return t('version.update_page.title_major')
  }
})

async function viewOldSummary() {
  projectStore.setUseStoredResults(true)
  await router.push({ name: 'summary-project-view' })
}

function acceptUpdate() {
  const updates = enrichedUpdates.value
  if (updates) {
    for (const eu of updates) {
      projectStore.versionUpdate(eu.update.part, eu.update.version)
    }
  }
  // Navigate to general page after accepting updates
  router.push({ name: 'general-project-view' })
}
</script>

<style lang="postcss" scoped>
.version-update-page {
  @apply flex items-center py-12;
  min-height: calc(100dvh - var(--app-top-inset, 0px) - var(--footer-min-height, 93px));
}

.version-update-card {
  @apply border border-neutral-300 bg-white;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.12),
    0 8px 16px -12px rgba(0, 0, 0, 0.2);
}

@supports not (height: 100dvh) {
  .version-update-page {
    min-height: calc(100vh - var(--app-top-inset, 0px) - var(--footer-min-height, 93px));
  }
}
</style>
